// /src/services/auth/index.js
import { magicLinkService } from './magic-links.js';
import { isValidEmail } from './utils.js';
import { BaseService } from '../base-service.js';
import store from '../../../state/index.js';
import { tokenProvider } from './token-provider.js';
import { Router } from '@vaadin/router';

// Constants
const SESSION_KEY = 'kablewy_session';
const USER_KEY = 'user';

// Valid roles
const VALID_ROLES = ['owner', 'admin', 'member', 'guest'];

// Cross-tab session sync
const SESSION_SYNC_CHANNEL = 'kablewy-session-sync';
const SESSION_SYNC_MESSAGE = {
  REQUEST: 'REQUEST_SESSION',
  RESPONSE: 'SESSION_RESPONSE',
  CLEAR: 'SESSION_CLEAR'
};

const generateTabId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

// Response validation schemas
const USER_SCHEMA = {
  required: ['id', 'email', 'orgId'],
  optional: ['status', 'role', 'preferences', 'metadata']
};

export class AuthService extends BaseService {
  constructor() {
    super();
    this.magicLinks = magicLinkService;
    this.basePath = '';
    this.initialized = false;
    this.sessionData = null;
    this.tabId = generateTabId();
    this.sessionChannel = null;
    this.sessionSyncInitialized = false;
    this.pendingSessionRequests = new Set();
    this.handleSessionChannelMessage = this.handleSessionChannelMessage.bind(this);
    this.initSessionSync();
  }

  // Validation helpers
  validateResponse(data, schema) {
    if (!data || typeof data !== 'object') return false;
    return schema.required.every(field => field in data);
  }

  validateRole(role, fallback) {
    const normalize = (value) => {
      const candidate = typeof value === 'string' ? value.trim().toLowerCase() : '';
      return candidate && VALID_ROLES.includes(candidate) ? candidate : undefined;
    };
    const primary = normalize(role);
    if (primary) return primary;
    const secondary = normalize(fallback);
    if (role && typeof role === 'string') {
      console.warn('Invalid role detected:', role);
    }
    return secondary;
  }

  initSessionSync() {
    if (this.sessionSyncInitialized) return;
    if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') {
      console.warn('[AuthService] BroadcastChannel not supported; multi-tab session sync disabled.');
      return;
    }
    try {
      this.sessionChannel = new BroadcastChannel(SESSION_SYNC_CHANNEL);
      this.sessionChannel.addEventListener('message', this.handleSessionChannelMessage);
      if (typeof window !== 'undefined') {
        window.addEventListener(
          'unload',
          () => {
            try {
              this.sessionChannel?.close();
            } catch (error) {
              console.warn('[AuthService] Error closing session channel during unload:', error);
            }
          },
          { once: true }
        );
      }
      this.sessionSyncInitialized = true;
    } catch (error) {
      console.warn('[AuthService] Failed to initialise session sync channel:', error);
      this.sessionChannel = null;
    }
  }

  async handleSessionChannelMessage(event) {
    const message = event?.data;
    if (!message || message.originTabId === this.tabId) return;

    if (message.type === SESSION_SYNC_MESSAGE.REQUEST) {
      await this.respondToSessionRequest(message);
      return;
    }

    if (message.type === SESSION_SYNC_MESSAGE.RESPONSE) {
      if (message.targetTabId !== this.tabId) return;
      if (!message.sessionData?.sessionToken) {
        this.resolvePendingSessionRequests(null);
        return;
      }
      this.resolvePendingSessionRequests(message.sessionData);
      if (!this.sessionData?.sessionToken) {
        await this.applySessionFromSync(message.sessionData);
      }
      return;
    }

    if (message.type === SESSION_SYNC_MESSAGE.CLEAR) {
      await this.applySessionClearFromSync();
    }
  }

  async respondToSessionRequest(message) {
    if (!this.sessionChannel || !message?.requesterId) return;
    try {
      const sessionSnapshot =
        this.sessionData?.sessionToken ? this.sessionData : tokenProvider.getSessionData?.();
      if (!sessionSnapshot?.sessionToken) {
        return;
      }
      this.sessionChannel.postMessage({
        type: SESSION_SYNC_MESSAGE.RESPONSE,
        originTabId: this.tabId,
        targetTabId: message.requesterId,
        sessionData: sessionSnapshot,
        timestamp: Date.now()
      });
    } catch (error) {
      console.warn('[AuthService] Failed to respond to session request:', error);
    }
  }

  requestSessionFromPeers(timeoutMs = 2000) {
    if (!this.sessionChannel) return Promise.resolve(null);
    // Avoid duplicate requests if one is already pending.
    if (this.pendingSessionRequests.size > 3) {
      console.warn('[AuthService] Too many pending session sync requests; skipping new request.');
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const requestEntry = { resolve, timeoutId: null };
      requestEntry.timeoutId = setTimeout(() => {
        this.pendingSessionRequests.delete(requestEntry);
        resolve(null);
      }, timeoutMs);

      this.pendingSessionRequests.add(requestEntry);

      try {
        this.sessionChannel.postMessage({
          type: SESSION_SYNC_MESSAGE.REQUEST,
          originTabId: this.tabId,
          requesterId: this.tabId,
          timestamp: Date.now()
        });
      } catch (error) {
        console.warn('[AuthService] Failed to broadcast session request:', error);
        clearTimeout(requestEntry.timeoutId);
        this.pendingSessionRequests.delete(requestEntry);
        resolve(null);
      }
    });
  }

  resolvePendingSessionRequests(sessionData) {
    if (this.pendingSessionRequests.size === 0) return;
    for (const entry of this.pendingSessionRequests) {
      clearTimeout(entry.timeoutId);
      try {
        entry.resolve(sessionData);
      } catch (error) {
        console.warn('[AuthService] Error resolving pending session request:', error);
      }
    }
    this.pendingSessionRequests.clear();
  }

  async applySessionFromSync(sessionData) {
    if (!sessionData?.sessionToken) return null;
    try {
      await this.setSessionData(sessionData, { skipBroadcast: true });
      return sessionData;
    } catch (error) {
      console.warn('[AuthService] Failed to apply session from peer tab:', error);
      return null;
    }
  }

  async applySessionClearFromSync() {
    if (!this.sessionData?.sessionToken) return;
    try {
      await this.clearSession({ skipBroadcast: true });
      this.resolvePendingSessionRequests(null);
    } catch (error) {
      console.warn('[AuthService] Failed to clear session from peer notification:', error);
    }
  }

  broadcastSessionClear() {
    if (!this.sessionChannel) return;
    try {
      this.sessionChannel.postMessage({
        type: SESSION_SYNC_MESSAGE.CLEAR,
        originTabId: this.tabId,
        timestamp: Date.now()
      });
      this.resolvePendingSessionRequests(null);
    } catch (error) {
      console.warn('[AuthService] Failed to broadcast session clear message:', error);
    }
  }

  async tryRestoreSessionFromPeers(timeoutMs = 2000) {
    const response = await this.requestSessionFromPeers(timeoutMs);
    if (response?.sessionToken) {
      if (!this.sessionData?.sessionToken) {
        await this.applySessionFromSync(response);
      }
      return response;
    }
    return null;
  }

  // Session Management
  async getSessionData() {
    try {
      const data = sessionStorage.getItem(SESSION_KEY);
      if (!data) return null;

      const session = JSON.parse(data);
      if (!session?.sessionToken || !session?.userData) {
        await this.clearSession({ skipBroadcast: true });
        return null;
      }

      const normalizedRole = this.validateRole(
        session.userData?.role,
        this.sessionData?.userData?.role
      );
      if (normalizedRole) {
        session.userData.role = normalizedRole;
      } else {
        delete session.userData.role;
      }

      return session;
    } catch (error) {
      console.error('Error reading session:', error);
      await this.clearSession({ skipBroadcast: true });
      return null;
    }
  }

  async setSessionData(data, { skipBroadcast = false } = {}) {
    try {
      // Log the data for debugging
      console.log('Auth service setSessionData called with:', data);
      
      // Validate required data
      if (!data) {
        console.error('setSessionData called with null or undefined data');
        throw new Error('Missing session data');
      }
      
      // Check for missing fields
      const missingFields = [];
      if (!data.sessionToken) missingFields.push('sessionToken');
      if (!data.email) missingFields.push('email');
      if (!data.orgId) missingFields.push('orgId');
      
      if (missingFields.length > 0) {
        console.error(`Missing required fields: ${missingFields.join(', ')}`, data);
        throw new Error(`Missing required session data: ${missingFields.join(', ')}`);
      }

      // Ensure we have userData, either from data or data.user
      let userData = data.userData || data.user;
      if (!userData) {
        console.warn('Missing user data, creating minimal user object');
        userData = {
          email: data.email,
          id: data.userId || 'unknown'
        };
      }

      const existingSession = this.sessionData || null;
      const previousRole = existingSession?.userData?.role;
      const resolvedRole = this.validateRole(userData.role ?? data.role, previousRole);
      if (resolvedRole) {
        userData.role = resolvedRole;
      } else {
        delete userData.role;
      }

      // Store session data
      const sessionData = {
        sessionToken: data.sessionToken,
        email: data.email,
        orgId: data.orgId,
        userData,
        lastActive: new Date().toISOString(),
        expiresAt: data.expiresAt || (Date.now() + 24 * 60 * 60 * 1000) // Default 24 hours
      };

      // Save to session storage
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      
      // Update token provider with expiry
      tokenProvider.setToken(data.sessionToken, sessionData.expiresAt);
      
      // Update the component state
      if (store) {
        store.setState({
          isAuthenticated: true,
          authenticated: true,
          sessionToken: data.sessionToken,
          orgId: data.orgId,
          user: userData
        });
      } else {
        console.warn('Store not available, unable to update application state');
      }
      
      this.sessionData = sessionData;
      this.initialized = true;

      if (!skipBroadcast) {
        this.resolvePendingSessionRequests(sessionData);
      }
      
      return sessionData;
    } catch (error) {
      console.error('Error setting session:', error);
      throw error;
    }
  }

  async clearSession(options = {}) {
    try {
      // Clear session storage
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(USER_KEY);
      
      // Clear token provider
      tokenProvider.clear();
      
      // Update the component state
      if (store) {
        console.log('AuthService: Clearing authentication state');
        store.setState({
          isAuthenticated: false,
          user: null
        });
      }

      this.sessionData = null;

      if (!options.skipBroadcast) {
        this.broadcastSessionClear();
      } else {
        this.resolvePendingSessionRequests(null);
      }
      
      return true;
    } catch (error) {
      console.error('Error clearing session:', error);
      return false;
    }
  }

  // Helper to get organization ID
  getOrganizationId() {
    try {
      // First try to get from session data
      const sessionData = this.sessionData || JSON.parse(sessionStorage.getItem(SESSION_KEY));
      if (sessionData && sessionData.orgId) {
        return sessionData.orgId;
      }
      
      // Legacy fallback to lastOrgId
      const legacyOrgId = sessionStorage.getItem('lastOrgId');
      if (legacyOrgId) {
        return legacyOrgId;
      }
      
      throw new Error('No organization ID found. Please log in again.');
    } catch (error) {
      throw new Error('No organization ID found. Please log in again.');
    }
  }

  isAuthenticated() {
    // Return true if a valid session token exists and has not expired
    try {
      // Prefer in-memory sessionData if present
      if (this.sessionData?.sessionToken) {
        return true;
      }
      // Fall back to the token provider (may have been initialised elsewhere)
      const token = tokenProvider.getToken();
      return !!token;
    } catch (e) {
      // On any unexpected error, assume not authenticated
      console.warn('AuthService.isAuthenticated error:', e);
      return false;
    }
  }

  // Authentication Methods
  async loginWithEmail(email) {
    if (!isValidEmail(email)) {
      throw new Error('Invalid email format');
    }

    try {
      // Try to get organization ID, will throw if not available
      const orgId = this.getOrganizationId();
      
      const response = await fetch(`${this.baseUrl}/org/${orgId}/magic-links`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to send magic link');
      }
      
      const result = await response.json();
      
      // Store email for verification
      sessionStorage.setItem('pendingAuth', JSON.stringify({
        email,
        method: 'magic-link',
        timestamp: Date.now()
      }));
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      // If the error is about missing org ID, redirect to login
      if (error.message && error.message.includes('No organization ID found')) {
        // Redirect to login page
        Router.go('/login');
      }
      throw error;
    }
  }

  async verifyMagicLink(token) {
    try {
      // Try to get organization ID, will throw if not available
      const orgId = this.getOrganizationId();
      
      const response = await fetch(`${this.baseUrl}/org/${orgId}/magic-links/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to verify magic link');
      }
      
      const result = await response.json();
      
      if (!result.success || !result.session_token) {
        throw new Error('Invalid verification response');
      }
      
      // Set session data with the response
      await this.setSessionData({
        sessionToken: result.session_token,
        email: result.user.email,
        orgId: result.user.orgId,
        userData: result.user,
        expiresAt: result.expires_at || (Date.now() + 24 * 60 * 60 * 1000) // 24 hour default
      });
      
      return {
        success: true,
        user: result.user
      };
    } catch (error) {
      console.error('Magic link verification error:', error);
      throw error;
    }
  }

  /**
   * Local/dev helper: Log in directly with an orgId and a session token.
   * Useful with `npm run token` output from backend.
   */
  async loginWithToken(orgId, sessionToken) {
    if (!orgId || !sessionToken) {
      throw new Error('orgId and sessionToken are required');
    }
    // Validate against backend so session shape matches server expectations
    const endpoint = `${this.baseUrl}/org/${orgId}/validate-session`;
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken })
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Token validation failed: ${response.status} ${text}`);
    }
    const validation = await response.json();
    if (!validation.valid) {
      throw new Error('Token is not valid');
    }
    // Persist session consistently
    await this.setSessionData({
      sessionToken,
      email: validation.email,
      orgId: validation.orgId,
      userData: validation.userData,
      expiresAt: validation.expiresAt || (Date.now() + 24 * 60 * 60 * 1000)
    });
    return { success: true };
  }

  async logout() {
    try {
      console.log('Logging out...');
      
      const sessionData = await this.getSessionData();
      let apiFailed = false;
      
      // Try to invalidate the session on the server if we have a token
      if (sessionData?.sessionToken) {
        try {
          // Get the organization ID
          const orgId = sessionData.orgId || this.getOrganizationId();
          
          // Call logout API to invalidate the session on the server
          const endpoint = `${this.baseUrl}/org/${orgId}/logout`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionData.sessionToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!response.ok) {
            console.warn(`Logout API returned status ${response.status}`);
            apiFailed = true;
          } else {
            console.log('Logout API call successful');
          }
        } catch (apiError) {
          // Continue with local logout even if API call fails
          console.warn('API logout failed, continuing with local logout:', apiError);
          apiFailed = true;
        }
      }
      
      // Clear local session
      await this.clearSession();
      
      // Reflect API failure status in return value (tests expect false on API failure)
      return apiFailed ? false : true;
    } catch (error) {
      console.error('Logout error:', error);
      // Still try to clear the local session
      await this.clearSession();
      return false;
    }
  }

  // Initialization and validation
  async init() {
    try {
      // 🔌 Auto-inject dev session for local debugging if missing
      if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && !sessionStorage.getItem(SESSION_KEY)) {
        console.log('🔌 Auto-injecting dev session for local debugging');
        const devSession = {
          sessionToken: 'dev-token-123',
          email: 'dev@localhost',
          orgId: 'dev-org-123',
          userData: { id: 'dev-user-123', email: 'dev@localhost', role: 'owner', orgId: 'dev-org-123' },
          expiresAt: Date.now() + 86400000
        };
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(devSession));
      }
      
      // Try to load session data
      let sessionData = await this.getSessionData();
      
      if (!sessionData) {
        const restoredSession = await this.tryRestoreSessionFromPeers();
        if (restoredSession?.sessionToken) {
          sessionData = restoredSession;
        }
      }
      
      if (!sessionData) {
        console.log('[DEBUG] Auth service init: No session data found');
        this.initialized = true;
        // Make sure we explicitly return the authentication state
        return { isAuthenticated: false };
      }
      
      // Set token for API calls
      tokenProvider.setToken(sessionData.sessionToken, sessionData.expiresAt);
      
      // Store the session data 
      this.sessionData = sessionData;
      
      // Try to validate session with server to get fresh user data
      try {
        console.log('[DEBUG] Auth service init: Validating existing session');
        const validationResult = await this.validateSession();
        
        if (validationResult.isValid) {
          console.log('[DEBUG] Auth service init: Session validated successfully');
          this.initialized = true;
          // Return clear authentication state
          return {
            isAuthenticated: true,
            userData: validationResult.userData,
            email: validationResult.email,
            orgId: validationResult.orgId
          };
        } else {
          console.warn('[DEBUG] Auth service init: Session validation failed, clearing session');
          // Session is invalid according to the server
          await this.clearSession();
          this.initialized = true;
          return { isAuthenticated: false };
        }
      } catch (validationError) {
        console.warn('[DEBUG] Auth service init: Error during session validation:', validationError);
        // Since there was an error in validation (likely network), 
        // use cached data rather than forcing logout
        
        // Update component state with cached data
        if (store) {
          console.log('[DEBUG] Auth service init: Using cached session data');
          store.setState({
            authenticated: true,
            isAuthenticated: true,
            sessionToken: sessionData.sessionToken,
            orgId: sessionData.orgId,
            user: sessionData.userData
          });
        }
        
        this.initialized = true;
        
        // Return clear authentication state from cached data
        return {
          isAuthenticated: true,
          userData: sessionData.userData,
          email: sessionData.email,
          orgId: sessionData.orgId
        };
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Clear session in case of errors
      await this.clearSession();
      this.initialized = true;
      return { isAuthenticated: false, error };
    }
  }

  // Validation endpoint to refresh user data
  async validateSession() {
    try {
      // Get the current session data
      const sessionData = await this.getSessionData();
      if (!sessionData?.sessionToken || !sessionData?.orgId) {
        console.error('No valid session to validate');
        return { isValid: false, error: 'No valid session' };
      }

      console.log(`[DEBUG] Validating session for orgId: ${sessionData.orgId}`);
      
      // Call the validation endpoint
      const endpoint = `${this.baseUrl}/org/${sessionData.orgId}/validate-session`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken: sessionData.sessionToken
        })
      });
      
      if (!response.ok) {
        console.error(`Session validation failed: ${response.status}`);
        return { isValid: false, error: `HTTP error ${response.status}` };
      }
      
      // Get validation result
      const validationData = await response.json();
      console.log('[DEBUG] Session validation response:', JSON.stringify(validationData));
      
      if (!validationData.valid) {
        console.error('Session not valid according to server');
        return { isValid: false, error: validationData.error || 'Invalid session' };
      }
      
      // Update session with fresh data from validation
      tokenProvider.updateFromValidation(validationData);
      
      // Update our internal reference
      this.sessionData = {
        sessionToken: validationData.sessionToken,
        email: validationData.email,
        orgId: validationData.orgId,
        userData: validationData.userData,
        lastActive: new Date().toISOString(),
        expiresAt: validationData.expiresAt
      };

      if (this.sessionData?.userData) {
        const fallbackRole = sessionData.userData?.role || this.sessionData.userData.role;
        const normalized = this.validateRole(this.sessionData.userData.role, fallbackRole);
        if (normalized) {
          this.sessionData.userData.role = normalized;
        } else {
          delete this.sessionData.userData.role;
        }
      }

      // Update component state
      if (store) {
        const normalizedRole = this.validateRole(validationData.userData?.role, sessionData.userData?.role);
        const nextUser = {
          ...(validationData.userData || {})
        };
        if (normalizedRole) {
          nextUser.role = normalizedRole;
        } else {
          delete nextUser.role;
        }
        store.setState({
          isAuthenticated: true,
          authenticated: true,
          sessionToken: validationData.sessionToken,
          orgId: validationData.orgId,
          user: nextUser
        });
      }

      return { 
        isValid: true, 
        userData: validationData.userData,
        email: validationData.email,
        orgId: validationData.orgId
      };
    } catch (error) {
      console.error('Session validation error:', error);
      return { isValid: false, error: error.message || 'Validation failed' };
    }
  }
}

export const authService = new AuthService();
